import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '@refinedev/core';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ErrorBoundary } from '../ErrorBoundary';
import { useAdminPageView } from '../../lib/admin-telemetry';

export function AdminLayout() {
  const { data: isAuthenticated, isLoading } = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();

  // ── THE ONE MOUNT POINT for admin page-view telemetry (spec 13.6) ────────
  // AdminLayout wraps EVERY authenticated route, so one call here covers all
  // 79 pages. Calling it per page would be 79 chances to forget - and a page
  // somebody forgot would report itself as unused, which is an instrument
  // whose silence is indistinguishable from its signal.
  //
  // It is called BEFORE the two early returns below, because React forbids a
  // conditional hook. That is also correct on the merits: the loading and
  // unauthenticated frames are not page views, and `useAdminPageView` emits on
  // a `location.pathname` CHANGE, so neither frame produces a row of its own.
  //
  // What leaves the browser is a route PATTERN, never a pathname (see
  // `lib/admin-telemetry.ts`), and it never rejects into this tree.
  //
  // The `authenticated` argument is load-bearing: `adminFetch` fails closed
  // without a Cognito session, so an ungated first render would burn the
  // landing page's one emit against a guaranteed 401.
  useAdminPageView(isAuthenticated?.authenticated === true);

  useEffect(() => {
    if (!isLoading && !isAuthenticated?.authenticated) {
      // 🔴 THE PATH `check()` COMPUTED, NOT A LITERAL. #3028 made `check()` return
      // `/login?reason=session_ended&to=<page>` and the login page read it — and
      // this line, the one that actually moves the browser, still said
      // a bare, literal login path. Seen at the UI on DEV minutes after the deploy:
      // new bundle, marker present, plain `/login`, no sentence. The unit tests
      // had checked the provider and the page, never the caller.
      // `replace`, so Back does not return to a page that bounces again.
      navigate(isAuthenticated?.redirectTo ?? '/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated?.authenticated) {
    return null;
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          {/* key on pathname so navigating to a new route remounts the
              boundary and clears any stuck error automatically. */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
